/**
 * Frontend diagnostics for the "stale bundle / React #301" rollback bug.
 *
 * Captures a navigation breadcrumb trail, intercepts errors (including
 * React's minified production errors like #301 = "Too many re-renders"),
 * and dumps the full context: URL, history, service workers, caches,
 * loaded asset hashes, build commit. The dump is logged to console and
 * stored in sessionStorage under `diag:lastError` so it survives the
 * automatic reload.
 */

interface NavCrumb {
  t: string;
  type: "init" | "pushState" | "replaceState" | "popstate" | "hashchange";
  from?: string;
  to: string;
}

const MAX_CRUMBS = 30;
const crumbs: NavCrumb[] = [];

function pushCrumb(type: NavCrumb["type"], to: string, from?: string) {
  crumbs.push({ t: new Date().toISOString(), type, from, to });
  if (crumbs.length > MAX_CRUMBS) crumbs.shift();
}

function decodeReactError(message: string): string | null {
  // Minified prod errors look like:
  //   "Minified React error #301; visit https://react.dev/errors/301?args[]=..."
  const m = message.match(/Minified React error #(\d+)/);
  if (!m) return null;
  const code = m[1];
  const known: Record<string, string> = {
    "301": "Too many re-renders. React limits the number of renders to prevent an infinite loop.",
    "300": "Rendered more hooks than during the previous render.",
    "310": "Rendered fewer hooks than expected.",
    "418": "Hydration failed because the initial UI does not match what was rendered on the server.",
    "423": "There was an error while hydrating but React was able to recover by switching to client rendering.",
    "425": "Text content does not match server-rendered HTML.",
  };
  return `React #${code}: ${known[code] ?? "(unknown code, see https://react.dev/errors/" + code + ")"}`;
}

async function snapshotServiceWorkers() {
  if (!("serviceWorker" in navigator)) return [];
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    return regs.map((r) => {
      const sw = r.active || r.waiting || r.installing;
      return {
        scope: r.scope,
        scriptURL: sw?.scriptURL ?? null,
        state: sw?.state ?? null,
        updateViaCache: r.updateViaCache,
      };
    });
  } catch (e) {
    return [{ error: String(e) }];
  }
}

async function snapshotCaches() {
  if (!("caches" in window)) return [];
  try {
    const keys = await window.caches.keys();
    const out = [];
    for (const name of keys) {
      try {
        const c = await window.caches.open(name);
        const reqs = await c.keys();
        out.push({ name, count: reqs.length, sample: reqs.slice(0, 5).map((r) => r.url) });
      } catch (e) {
        out.push({ name, error: String(e) });
      }
    }
    return out;
  } catch (e) {
    return [{ error: String(e) }];
  }
}

function snapshotLoadedAssets() {
  // Identifies which JS/CSS bundle hashes the page is actually running.
  const scripts = Array.from(document.querySelectorAll("script[src]"))
    .map((s) => (s as HTMLScriptElement).src)
    .filter((s) => s.includes("/assets/"));
  const styles = Array.from(document.querySelectorAll("link[rel=stylesheet]"))
    .map((l) => (l as HTMLLinkElement).href)
    .filter((s) => s.includes("/assets/"));
  return { scripts, styles };
}

export interface DiagDump {
  capturedAt: string;
  trigger: string;
  message: string;
  decoded: string | null;
  stack?: string;
  build: { commit: string; time: string };
  url: string;
  referrer: string;
  navCrumbs: NavCrumb[];
  performanceNav: PerformanceNavigationTiming[];
  loadedAssets: { scripts: string[]; styles: string[] };
  serviceWorkers: unknown;
  caches: unknown;
  controller: { hasController: boolean; scriptURL: string | null };
  storage?: StorageEstimate;
  ua: string;
}

export async function buildDiagDump(trigger: string, err: unknown): Promise<DiagDump> {
  const error = err instanceof Error ? err : new Error(String(err));
  const message = error.message ?? "(no message)";
  const navTimings = (performance.getEntriesByType("navigation") as PerformanceNavigationTiming[]) ?? [];

  const dump: DiagDump = {
    capturedAt: new Date().toISOString(),
    trigger,
    message,
    decoded: decodeReactError(message),
    stack: error.stack,
    build: {
      commit: typeof __BUILD_COMMIT__ !== "undefined" ? __BUILD_COMMIT__ : "dev",
      time: typeof __BUILD_TIME__ !== "undefined" ? __BUILD_TIME__ : "",
    },
    url: window.location.href,
    referrer: document.referrer,
    navCrumbs: [...crumbs],
    performanceNav: navTimings,
    loadedAssets: snapshotLoadedAssets(),
    serviceWorkers: await snapshotServiceWorkers(),
    caches: await snapshotCaches(),
    controller: {
      hasController: !!navigator.serviceWorker?.controller,
      scriptURL: navigator.serviceWorker?.controller?.scriptURL ?? null,
    },
    ua: navigator.userAgent,
  };

  if ("storage" in navigator && "estimate" in navigator.storage) {
    try {
      dump.storage = await navigator.storage.estimate();
    } catch { /* noop */ }
  }

  return dump;
}

function persistDump(dump: DiagDump) {
  try {
    sessionStorage.setItem("diag:lastError", JSON.stringify(dump));
  } catch { /* quota or disabled */ }
}

function logDump(dump: DiagDump) {
  // Grouped + readable in console.
  /* eslint-disable no-console */
  console.groupCollapsed(
    `%c[DIAG ${dump.trigger}]%c ${dump.decoded ?? dump.message}`,
    "color:#fff;background:#dc2626;padding:2px 6px;border-radius:3px;font-weight:bold",
    "color:inherit",
  );
  console.log("Captured at:", dump.capturedAt);
  console.log("Build commit:", dump.build.commit, "(", dump.build.time, ")");
  console.log("URL:", dump.url);
  console.log("Referrer:", dump.referrer);
  console.log("Loaded assets:", dump.loadedAssets);
  console.log("Service workers:", dump.serviceWorkers);
  console.log("SW controller:", dump.controller);
  console.log("Caches:", dump.caches);
  console.log("Navigation breadcrumbs:", dump.navCrumbs);
  console.log("Performance navigation:", dump.performanceNav);
  if (dump.stack) console.log("Stack:\n" + dump.stack);
  console.log("Full dump (copy):", dump);
  console.groupEnd();
  /* eslint-enable no-console */
}

export async function reportError(trigger: string, err: unknown) {
  const dump = await buildDiagDump(trigger, err);
  logDump(dump);
  persistDump(dump);
  return dump;
}

export function getLastErrorDump(): DiagDump | null {
  try {
    const raw = sessionStorage.getItem("diag:lastError");
    return raw ? (JSON.parse(raw) as DiagDump) : null;
  } catch {
    return null;
  }
}

export function clearLastErrorDump() {
  try { sessionStorage.removeItem("diag:lastError"); } catch { /* noop */ }
}

let installed = false;

export function installDiagnostics() {
  if (installed) return;
  installed = true;

  pushCrumb("init", window.location.href);

  // History instrumentation — captures every SPA navigation.
  const wrap = (key: "pushState" | "replaceState") => {
    const orig = history[key].bind(history);
    history[key] = function (
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ) {
      const from = window.location.href;
      const ret = orig(data as never, unused, url as never);
      pushCrumb(key, window.location.href, from);
      return ret;
    } as typeof history.pushState;
  };
  wrap("pushState");
  wrap("replaceState");

  window.addEventListener("popstate", () => {
    pushCrumb("popstate", window.location.href);
  });
  window.addEventListener("hashchange", (e) => {
    pushCrumb("hashchange", e.newURL, e.oldURL);
  });

  // Global error capture — picks up React #301 even outside ErrorBoundary
  // (e.g. async errors during render-triggered effects).
  window.addEventListener("error", (e) => {
    void reportError("window.error", e.error ?? e.message);
  });
  window.addEventListener("unhandledrejection", (e) => {
    void reportError("unhandledrejection", e.reason);
  });
}
